import { Injectable, inject } from '@angular/core';

import { difference, orderBy, uniq } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { DefinedTermMeta as DefinedTermMetaResponse } from '../../../generated/graphql-models';
import { GET_DEFINE_TERM_META } from '../../shared/constants/gql-queries';
import { GraphQLService } from '../../shared/services/graphql.service';
import { DefinedTermMetaWithStatistics, TermStatistics } from '../models/defined-term-meta.model';

@Injectable({ providedIn: 'root' })
export class DefinedTermMetaService {
  private readonly graphQlService = inject(GraphQLService);

  public getAllByTerm(definedTerm: string): Observable<DefinedTermMetaWithStatistics> {
    return this.graphQlService
      .getSingleQuery<
        DefinedTermMetaResponse,
        { definedTerm: string }
      >(GET_DEFINE_TERM_META, { definedTerm })
      .pipe(map((metaResponse) => this.viewModelMapper(definedTerm, metaResponse)));
  }

  public viewModelMapper(
    term: string,
    metaResponse: DefinedTermMetaResponse
  ): DefinedTermMetaWithStatistics {
    try {
      const synonyms = uniq(
        metaResponse.synonymns
          .map((synonym) => synonym.definedTerm)
          .filter((synonym) => synonym !== term)
      );
      const relatedTerms = uniq(
        metaResponse.defineTermVarians
          .map((term) => term.definedTerm)
          .filter((relatedTerm) => relatedTerm !== term)
      );

      return {
        meta: {
          synonyms: orderBy(synonyms),
          relatedTerms: orderBy(difference(relatedTerms, synonyms)),
          term,
        },
        statistics: [
          ...metaResponse.synonymns,
          ...metaResponse.defineTermVarians,
        ].reduce<TermStatistics>((acc, term) => {
          acc[term.definedTerm] = `${term.documentCount} docs`;
          return acc;
        }, {}),
      };
    } catch (err) {
      console.error(err);
    }
  }
}
